/**
 * XP and level rules.
 *
 * XP itself is an append-only ledger in the database (`xp_events`); this module
 * holds only the pure functions that turn a total into a level. Keeping the
 * curve here — rather than inside a SQL expression or a React component — means
 * the API and the UI can never disagree about what level a learner is.
 */

export interface LevelDefinition {
  readonly level: number;
  /** Cumulative XP required to reach this level. */
  readonly minXp: number;
  readonly title: string;
}

/**
 * An explicit table rather than a formula: the curve is product content, and a
 * table is easier to tune than an exponent. Gaps widen gradually so that early
 * levels arrive quickly and later ones represent real work.
 */
export const LEVELS: readonly LevelDefinition[] = [
  { level: 1, minXp: 0, title: 'Newcomer' },
  { level: 2, minXp: 100, title: 'Query Novice' },
  { level: 3, minXp: 250, title: 'SELECT Apprentice' },
  { level: 4, minXp: 450, title: 'Filter Wrangler' },
  { level: 5, minXp: 700, title: 'Aggregator' },
  { level: 6, minXp: 1000, title: 'Join Practitioner' },
  { level: 7, minXp: 1400, title: 'Data Cleaner' },
  { level: 8, minXp: 1900, title: 'Time Series Analyst' },
  { level: 9, minXp: 2500, title: 'Subquery Specialist' },
  { level: 10, minXp: 3200, title: 'CTE Architect' },
  { level: 11, minXp: 4100, title: 'Window Function Adept' },
  { level: 12, minXp: 5200, title: 'Cohort Analyst' },
  { level: 13, minXp: 6500, title: 'Retention Expert' },
  { level: 14, minXp: 8000, title: 'Analytics Engineer' },
  { level: 15, minXp: 9800, title: 'PostgreSQL Craftsman' },
  { level: 16, minXp: 12000, title: 'Insight Builder' },
  { level: 17, minXp: 14600, title: 'Query Optimiser' },
  { level: 18, minXp: 17600, title: 'Analytics Lead' },
  { level: 19, minXp: 21000, title: 'Data Strategist' },
  { level: 20, minXp: 25000, title: 'PostgreSQL Master' },
];

export const MAX_LEVEL = LEVELS[LEVELS.length - 1]!.level;

export function levelForXp(totalXp: number): LevelDefinition {
  const xp = Math.max(0, Math.floor(totalXp));
  let current = LEVELS[0]!;
  for (const definition of LEVELS) {
    if (xp >= definition.minXp) current = definition;
    else break;
  }
  return current;
}

export interface LevelProgress {
  readonly level: number;
  readonly title: string;
  readonly totalXp: number;
  readonly currentLevelMinXp: number;
  /** Null once the learner is at the maximum level. */
  readonly nextLevelMinXp: number | null;
  readonly xpIntoLevel: number;
  readonly xpForNextLevel: number | null;
  /** 0..1 through the current level; 1 at max level. */
  readonly ratio: number;
}

export function levelProgress(totalXp: number): LevelProgress {
  const xp = Math.max(0, Math.floor(totalXp));
  const current = levelForXp(xp);
  const next = LEVELS.find((l) => l.level === current.level + 1) ?? null;

  const xpIntoLevel = xp - current.minXp;
  const xpForNextLevel = next ? next.minXp - current.minXp : null;

  return {
    level: current.level,
    title: current.title,
    totalXp: xp,
    currentLevelMinXp: current.minXp,
    nextLevelMinXp: next?.minXp ?? null,
    xpIntoLevel,
    xpForNextLevel,
    ratio: xpForNextLevel === null ? 1 : Math.min(1, xpIntoLevel / xpForNextLevel),
  };
}

/**
 * XP actually paid out for a solve, after deducting the penalty for hints the
 * learner revealed. Never drops below a floor, because finishing a hard problem
 * with help is still worth something.
 */
export function xpAfterHintPenalty(baseXp: number, hintPenaltyTotal: number): number {
  const floor = Math.ceil(baseXp * 0.25);
  return Math.max(floor, baseXp - Math.max(0, hintPenaltyTotal));
}
